import { useCallback, useEffect, useState } from "react";
import { Star, MessageSquare, Gift, TrendingUp } from "lucide-react";
import { PageHeader } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, isAdminRole, isManagerRole } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Stars } from "@/components/delivery/RatingBox";
import { Distribution, DailyChart } from "@/components/ratings/RatingCharts";
import { RatingList } from "@/components/ratings/RatingList";
import { CouponSettingsCard } from "@/components/ratings/CouponSettingsCard";
import { toast } from "sonner";

const PERIODS = [[7, "7 dias"], [30, "30 dias"], [90, "90 dias"], [0, "Tudo"]];
const fmt = (v) => (v == null ? "—" : v.toFixed(1).replace(".", ","));

function Kpi({ icon: Icon, label, value, extra, testId }) {
  return (
    <div className="card-surface p-4 space-y-1">
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</p>
      <p className="text-2xl font-bold" data-testid={testId}>{value}</p>
      {extra}
    </div>
  );
}

export default function Ratings() {
  const { session } = useAuth();
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/ratings/summary?days=${days}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [days]);
  useEffect(() => { load(); }, [load]);
  const onReplied = (id, reply) => setD((x) => ({ ...x, latest: x.latest.map((r) => (r.order_id === id ? { ...r, reply } : r)) }));
  return (
    <>
      <PageHeader eyebrow="Delivery" title="Avaliações" subtitle="Notas e comentários dos clientes depois da entrega."
        actions={<div className="flex rounded-lg border p-1">{PERIODS.map(([v, l]) => (
          <button key={v} onClick={() => setDays(v)} className={cn("rounded-md px-3 py-1.5 text-sm transition-colors", days === v ? "bg-primary text-primary-foreground" : "hover:bg-muted")} data-testid={`rating-period-${v}`}>{l}</button>
        ))}</div>} />
      {d && (<>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Star} label="Média geral da loja" value={fmt(d.overall.avg)} testId="rating-overall-avg" extra={<p className="flex items-center gap-1 text-xs text-muted-foreground"><Stars value={Math.round(d.overall.avg || 0)} size="h-3 w-3" />{d.overall.count} avaliações</p>} />
          <Kpi icon={TrendingUp} label="Média no período" value={fmt(d.period.avg)} testId="rating-period-avg" extra={<p className="text-xs text-muted-foreground">{d.period.five_pct}% deram 5 estrelas</p>} />
          <Kpi icon={MessageSquare} label="Avaliações no período" value={d.period.count} testId="rating-period-count" />
          <Kpi icon={Gift} label="Cupons de agradecimento" value={`${d.coupons.used}/${d.coupons.issued}`} testId="rating-coupons" extra={<p className="text-xs text-muted-foreground">usados / emitidos no período</p>} />
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <Distribution dist={d.distribution} total={d.period.count} />
          {d.series.length ? <DailyChart series={d.series} /> : <div className="card-surface p-5 text-sm text-muted-foreground grid place-items-center">Escolha 7, 30 ou 90 dias para ver a nota por dia.</div>}
        </div>
        {isAdminRole(session.user) && <CouponSettingsCard initial={d.coupon_settings} />}
        <RatingList items={d.latest} store={session.tenant.nome_fantasia} slug={session.tenant.menu_slug} canReply={isManagerRole(session.user)} onReplied={onReplied} />
      </>)}
    </>
  );
}
